<script lang="ts">
  import { format, parseISO } from "date-fns";
  import ClockIcon from "$lib/icons/ClockIcon.svelte";
  import PeopleIcon from "$lib/icons/PeopleIcon.svelte";
  import TournamentPlayerItem from "$lib/components/ModalContents/Tournament/TournamentPlayerItem.svelte";
  import { userService } from "$lib/services/user.service";
  import type { ITournament } from "$lib/interfaces/ITournament.js";
  import Countdown from "$lib/elements/Countdown.svelte";
  import CurrencySymbol from "$lib/elements/CurrencySymbol.svelte";
  import NumberFormat from "$lib/components/NumberFormat.svelte";
  import { translate } from "$lib/i18n";
  import { openDialog } from "$lib/stores/dialogStores";

  // State variables
  let tournament = $state<ITournament | null>(null);
  let loading = $state(true);
  let error = $state<Error | null>(null);
  let observeMe = $state<HTMLElement | null>(null);
  let observer = $state<IntersectionObserver | null>(null);
  let inView = $state(false);
  let isBottom = $state(true);
  let lastYPosition = $state<number | null>(null);
  let initTournament = $state(false);

  // Fetch tournament data
  async function getTournaments() {
    try {
      loading = true;
      const response = await userService.getTournaments();
      if (response && response.data) {
        tournament = response.data.data;
      } else {
        throw new Error('Failed to fetch tournaments');
      }
    } catch (err) {
      console.error(err);
      error = err instanceof Error ? err : new Error('Unknown error');
    } finally {
      loading = false;
    }
  }

  // Format date using date-fns instead of moment
  function formatDate(dateString: string) {
    try {
      return format(parseISO(dateString), 'yyyy-MM-dd');
    } catch (error) {
      return dateString;
    }
  }

  // Setup intersection observer
  $effect(() => {
    // Initial data load
    getTournaments();

    // Setup observer after a delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const options = {
        root: null, // Use the viewport as the root
        threshold: 0.5 // Trigger when at least 50% of the element is visible
      };

      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (lastYPosition !== null) {
            isBottom = entry.boundingClientRect.y >= lastYPosition;
          }
          lastYPosition = entry.boundingClientRect.y;
          inView = entry.isIntersecting;
        });
      }, options);

      if (observeMe) {
        observer.observe(observeMe);
        initTournament = true;
      }
    }, 1500);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        observer.disconnect();
      }
    };
  });
</script>

<div class="flex flex-col gap-5 overflow-y-auto max-h-[90vh] no-scrollbar">
  {#if loading}
    <div class="min-h-[40vh] flex justify-center items-center col-span-full">
      <enhanced:img src="$lib/logo.svg?enhanced" class="animate-bounce" width="140" alt="Forza.bet"/>
    </div>
  {:else if error}
    <div class="text-white">{$translate('Failed to fetch tournaments')}</div>
  {:else if tournament}
    <div class="relative">
      <img src={tournament.image.fileUrl || "/placeholder.svg"} class="rounded-xl h-40 w-full object-cover" alt="Tournament Banner">
      <div class="absolute left-2 top-2 bg-primary-fire text-white rounded-md px-2 py-1 text-sm">
        {$translate('Started')}: {formatDate(tournament.startsAt)}
      </div>
      <div class="absolute bottom-0 -left-7 w-56 flex">
        <img class="" src="/illustrations/tournament-frame.png" alt="">
        <div class="flex justify-center items-center flex-col gap-1 absolute left-14 bottom-10">
          <span class="gradient-text-fire text-xs font-bold text-nowrap">{$translate('Tournament Prize')}</span>
          <span class="gradient-text-fire text-2xl font-bold duration-1000 animate-pulse transition-all">
            <CurrencySymbol currency={tournament.currency.name} /> 
            <NumberFormat value={tournament.prizeAmount} digitsInfo="1.0-0" />
          </span>
        </div>
      </div>
    </div>

    <div class="flex items-center text-sm">
      <ClockIcon />
      <span class="text-primary-lightBlue mx-2">{$translate('Ends in')}</span>
      <span class="text-white"><Countdown date={tournament.endsAt} /></span>
    </div>

    <div class="bg-primary-dark rounded-full w-full py-1.5 px-4 text-sm text-primary-lightBlue flex items-center">
      <PeopleIcon /> 
      <span class="pl-1">{$translate('Players')}</span>
      <span class="text-white ml-2">{tournament.participantCount}</span>
    </div>

    <div class="flex items-center gap-2">
      <h2 class="text-white">{tournament.title}</h2>
      <svg 
        on:click={() => openDialog('TournamentsContentModal')} 
        class="w-[18px] h-[18px] text-white cursor-pointer" 
        aria-hidden="true" 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        fill="none" 
        viewBox="0 0 24 24"
      >
        <path 
          stroke="currentColor" 
          stroke-linecap="round" 
          stroke-linejoin="round" 
          stroke-width="2" 
          d="M9.529 9.988a2.502 2.502 0 1 1 5 .191A2.441 2.441 0 0 1 12 12.582V14m-.01 3.008H12M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    </div>
    <p class="text-primary-lightBlue text-sm">{$translate('Join us for thrilling competition and grand prizes!')}</p>

    <div class="grid grid-cols-1 gap-2 bg-primary-dark rounded-2xl p-4">
      <div class="col-span-1 grid grid-cols-12 px-2 pb-2 text-white text-sm">
        <div class="col-span-2">{$translate('Place')}</div>
        <div class="col-span-4">{$translate('Player')}</div>
        <div class="col-span-3">{$translate('Points')}</div>
        <div class="col-span-3 text-right">{$translate('Prize')}</div>
      </div>
      
      {#each tournament.placements as player}
        {#if player.isMe}
          <div bind:this={observeMe}>
            <TournamentPlayerItem isMe={player.isMe} player={player} tournament={tournament}/>
          </div>
        {:else}
          <TournamentPlayerItem isMe={player.isMe} player={player} tournament={tournament}/>
        {/if}
      {/each}

      {#if tournament.myPlacement && !inView && initTournament}
        <div class="{isBottom ? 'bottom-10' : 'bottom-[calc(100%-10px)]'} sticky z-10 h-0">
          <TournamentPlayerItem 
            isMe={true} 
            player={tournament.myPlacement} 
            tournament={tournament}
          />
        </div>
      {/if}
    </div>
  {/if}
</div>